import React from 'react';
import { Radio, RadioGroup } from 'react-aria-components';
import { Text, TextField, cn } from '@chatsdet/ui';

type VisualCheckpointNodeConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

// masks and resolvedTarget are machine-owned: never read or written here, so
// a save does not send them and the API keeps the stored values.
export const VisualCheckpointNodeConfig: React.FC<VisualCheckpointNodeConfigProps> = ({
  localData,
  setLocalData,
}) => {
  const name = (localData.name as string) ?? '';
  const target = (localData.target as string) ?? '';
  const expectedState = (localData.expectedState as string) ?? '';
  const severity: 'record' | 'fail' = localData.severity === 'fail' ? 'fail' : 'record';

  const set = (key: string, value: string) =>
    setLocalData((prev) => {
      const next = { ...prev, [key]: value };
      // The schema requires title and description; mirror canary-spec emit.
      const title = `Visual checkpoint: ${String(next.name ?? '')}`;
      return { ...next, title, description: title };
    });

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Text size="xs" className="text-muted-foreground mb-1 block">
          Name
        </Text>
        <TextField
          value={name}
          onChange={(v) => set('name', v ?? '')}
          placeholder="Order confirmation"
        />
        <Text size="xs" className="text-muted-foreground mt-1 block">
          Unique within this flow.
        </Text>
      </div>

      <div>
        <Text size="xs" className="text-muted-foreground mb-1 block">
          Target (optional)
        </Text>
        <TextField
          value={target}
          onChange={(v) => set('target', v ?? '')}
          placeholder="the order summary card"
        />
        <Text size="xs" className="text-muted-foreground mt-1 block">
          Describe the part of the page in plain English. Empty captures the whole page.
        </Text>
      </div>

      <div>
        <Text size="xs" className="text-muted-foreground mb-1 block">
          Expected state (optional)
        </Text>
        <TextField
          value={expectedState}
          onChange={(v) => set('expectedState', v ?? '')}
          placeholder="Shows the order total and a green confirmed badge"
        />
      </div>

      <div>
        <Text size="xs" className="text-muted-foreground mb-1 block">
          Severity
        </Text>
        <RadioGroup
          aria-label="Severity"
          value={severity}
          onChange={(v) => set('severity', v)}
          className="flex flex-col gap-2"
        >
          <Radio
            value="record"
            className={({ isSelected, isFocusVisible }) =>
              cn(
                'flex cursor-pointer flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left transition-colors',
                'hover:bg-muted/40',
                isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-card',
                isFocusVisible && 'outline-none ring-2 ring-primary ring-offset-1'
              )
            }
          >
            <span className="text-sm font-medium text-foreground">Record</span>
            <span className="text-xs text-muted-foreground">
              Keep the capture for review; the step passes.
            </span>
          </Radio>
          <Radio
            value="fail"
            className={({ isSelected, isFocusVisible }) =>
              cn(
                'flex cursor-pointer flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left transition-colors',
                'hover:bg-muted/40',
                isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-card',
                isFocusVisible && 'outline-none ring-2 ring-primary ring-offset-1'
              )
            }
          >
            <span className="text-sm font-medium text-foreground">Fail</span>
            <span className="text-xs text-muted-foreground">
              Fail the step when the page visibly changed.
            </span>
          </Radio>
        </RadioGroup>
      </div>
    </div>
  );
};
